import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    collection,
    onSnapshot,
    query,
    where,
    orderBy,
    doc,
    getDoc,
    getDocs,
    documentId,
    limit
} from 'firebase/firestore';
import { Calendar, Loader2, Info, Users, Filter, ChevronDown, Check, Clock, BookOpen, UserCheck, AlertTriangle } from 'lucide-react';

/**
 * Composant de consultation de l'emploi du temps pour un élève ou ses parents.
 * @param {object} props.db - Instance de Firebase Firestore.
 * @param {string} props.schoolId - ID de l'école.
 * @param {string} props.currentUserId - L'UID d'authentification Firebase de l'utilisateur (élève ou parent).
 * @param {string} props.userRole - Le rôle de l'utilisateur ('student', 'parent', etc.).
 * @param {function} props.setError - Fonction pour définir un message d'erreur.
 */
const MyEmploiDuTemps = ({ db, setError, schoolId, currentUserId, userRole }) => {
    console.count('MyEmploiDuTemps Render'); // Pour compter les rendus du composant

    // --- CONSTANTES ---
    const JoursDeLaSemaine = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
    const FIRESTORE_IN_QUERY_LIMIT = 10;

    // --- États des données ---
    const [edtItems, setEdtItems] = useState({}); // { 'Lundi': [...items], ... }
    const [isLoading, setIsLoading] = useState(true);
    const [isResolvingIdentity, setIsResolvingIdentity] = useState(true);

    // --- États Spécifiques Parent ---
    const [children, setChildren] = useState([]); // Liste des enfants
    const [selectedChildId, setSelectedChildId] = useState(''); // ID du Document Élève sélectionné
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    // --- États de Résolution Finale ---
    const [classIdToFetch, setClassIdToFetch] = useState(''); // ID FINAL de la Classe (sera un 'classeId')
    const [classNameToDisplay, setClassNameToDisplay] = useState('N/A');

    const isParentRole = userRole === 'parent';

    // --- Configuration de l'Année Scolaire Actuelle (Basée sur l'environnement précédent) ---
    const getCurrentSchoolYear = () => {
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth();
        return currentMonth >= 8 ? `${currentYear}-${currentYear + 1}` : `${currentYear - 1}-${currentYear}`;
    };
    const currentSchoolYear = useMemo(getCurrentSchoolYear, []);

    // ----------------------------------------------------------------------------------
    // --- LOGIQUE D'IDENTITÉ: Résolution Parent/Élève vers Class ID ---
    // ----------------------------------------------------------------------------------

    const resolveIdentity = useCallback(async () => {
        console.count('resolveIdentity useCallback invoked'); // Pour compter les exécutions de resolveIdentity

        if (!currentUserId || !schoolId || !db) {
            console.log("[DEBUG - Identity] Skipping resolveIdentity: missing currentUserId, schoolId, or db.");
            return;
        }

        setIsResolvingIdentity(true);
        // Toujours réinitialiser l'état au début de la résolution
        setClassIdToFetch('');
        setClassNameToDisplay('N/A');
        setError(''); // Effacer les erreurs précédentes

        let resolvedClassIdForLog = ''; // Variable locale pour le log précis de cette exécution
        let resolvedClassNameForLog = 'N/A'; // Variable locale pour le log précis de cette exécution

        try {
            if (isParentRole) {
                // --- LOGIQUE PARENT ---
                const userDocRef = doc(db, 'users', currentUserId);
                const userDocSnap = await getDoc(userDocRef);

                const linkedEleveIds = userDocSnap.exists() && userDocSnap.data().childrenEleveIds
                    ? userDocSnap.data().childrenEleveIds.filter(id => id && typeof id === 'string')
                    : [];

                if (linkedEleveIds.length === 0) {
                    setChildren([]);
                    console.log(`[DEBUG - Parent] Aucun enfant lié trouvé pour l'utilisateur ${currentUserId}.`);
                    return; // Sortir si aucun enfant n'est lié
                }

                // 1. Récupérer les détails des élèves liés
                const elevesCollectionRef = collection(db, 'schools', schoolId, 'eleves');
                const elevesSnap = await getDocs(query(elevesCollectionRef, where(documentId(), 'in', linkedEleveIds.slice(0, FIRESTORE_IN_QUERY_LIMIT))));

                // 2. Récupérer les inscriptions pour obtenir les Class IDs
                const enrollmentsCollectionRef = collection(db, 'schools', schoolId, 'enrollments');
                const qEnrollments = query(
                    enrollmentsCollectionRef,
                    where('studentId', 'in', linkedEleveIds.slice(0, FIRESTORE_IN_QUERY_LIMIT)),
                    where('schoolYear', '==', currentSchoolYear)
                );
                const enrollmentsSnap = await getDocs(qEnrollments);
                const enrollmentMap = new Map();
                enrollmentsSnap.forEach(d => enrollmentMap.set(d.data().studentId, d.data().classId));

                const allChildren = [];
                for (const eleveDoc of elevesSnap.docs) {
                    const eleveId = eleveDoc.id;
                    const classId = enrollmentMap.get(eleveId); // classId vient de enrollments
                    let className = 'Chargement...';

                    if (classId) {
                        const classDocRef = doc(db, `schools/${schoolId}/classes`, classId);
                        const classDocSnap = await getDoc(classDocRef);
                        className = classDocSnap.exists() ? classDocSnap.data().nom : 'Inconnue';
                    } else {
                        className = 'Non Inscrit';
                    }

                    allChildren.push({
    id: eleveId,
    name: eleveDoc.data().name || eleveId, // Utilise directement le champ 'name', ou l'eleveId comme fallback
    classId: classId,
    className: className
});

                }

                setChildren(allChildren);
                if (allChildren.length > 0) {
                    const initialChild = allChildren.find(c => c.classId) || allChildren[0];
                    setSelectedChildId(initialChild.id);
                    resolvedClassIdForLog = initialChild.classId || '';
                    resolvedClassNameForLog = initialChild.className || 'N/A';
                    setClassIdToFetch(resolvedClassIdForLog); // Ici, c'est l'ID de la classe de l'enfant
                    setClassNameToDisplay(resolvedClassNameForLog);
                    console.log(`[DEBUG - Parent] Enfant initial sélectionné: ${initialChild.name}, Eleve ID: ${initialChild.id}, ID de la classe à charger: ${resolvedClassIdForLog}, Nom de la classe: ${resolvedClassNameForLog}`);
                } else {
                    console.log(`[DEBUG - Parent] Aucun enfant avec une inscription active n'a pu être trouvé.`);
                }

            } else {
                // --- LOGIQUE ÉLÈVE (STUDENT) ---

                // 1. Trouver l'ID du document élève (Eleve Doc ID)
                const elevesCollectionRef = collection(db, 'schools', schoolId, 'eleves');
                const qEleve = query(elevesCollectionRef, where('authId', '==', currentUserId), limit(1));
                const snapEleve = await getDocs(qEleve);

                if (snapEleve.empty) {
                    setError("Aucun dossier élève trouvé pour votre compte.");
                    console.log(`[DEBUG - Student] Aucun dossier élève trouvé pour currentUserId: ${currentUserId}.`);
                    return;
                }
                const resolvedEleveId = snapEleve.docs[0].id; // L'ID du document élève
                console.log(`[DEBUG - Student] Dossier élève résolu: ${resolvedEleveId} pour authId: ${currentUserId}`);

                // 2. Trouver la classe actuelle (Class ID)
                const enrollmentCollectionRef = collection(db, 'schools', schoolId, 'enrollments');
                const activeEnrollmentQuery = query(
                    enrollmentCollectionRef,
                    where('studentId', '==', resolvedEleveId),
                    where('schoolYear', '==', currentSchoolYear),
                    limit(1)
                );
                const enrollmentSnapshot = await getDocs(activeEnrollmentQuery);

                if (!enrollmentSnapshot.empty) {
                    const classId = enrollmentSnapshot.docs[0].data().classId; // classId vient de enrollments
                    if (classId) {
                        const classDocRef = doc(db, `schools/${schoolId}/classes`, classId);
                        const classDocSnap = await getDoc(classDocRef);
                        const className = classDocSnap.exists() ? classDocSnap.data().nom : 'Classe Inconnue';

                        resolvedClassIdForLog = classId;
                        resolvedClassNameForLog = className;
                        setClassIdToFetch(resolvedClassIdForLog); // Ici, c'est l'ID de la classe de l'élève
                        setClassNameToDisplay(resolvedClassNameForLog);
                        console.log(`[DEBUG - Student] ID Élève: ${resolvedEleveId}, ID de la classe à charger: ${resolvedClassIdForLog}, Nom de la classe: ${resolvedClassNameForLog}`);
                    } else {
                        resolvedClassNameForLog = 'Classe Non Spécifiée';
                        setClassNameToDisplay(resolvedClassNameForLog);
                        console.log(`[DEBUG - Student] ID de la classe est null/undefined pour l'élève ID: ${resolvedEleveId}.`);
                    }
                } else {
                    resolvedClassNameForLog = 'Non Inscrit pour cette année';
                    setClassNameToDisplay(resolvedClassNameForLog);
                    console.log(`[DEBUG - Student] Aucune inscription active trouvée pour l'élève ID: ${resolvedEleveId} pour l'année scolaire ${currentSchoolYear}.`);
                }
            }
        } catch (err) {
            console.error("Erreur lors de la résolution de l'identité pour l'EDT:", err);
            setError("Erreur critique lors de la résolution de votre identité et de votre classe.");
        } finally {
            setIsResolvingIdentity(false);
            // Ce log reflétera VRAIMENT le résultat de cette exécution spécifique de resolveIdentity
            console.log(`[DEBUG - Identity Resolved - FINALLY OUTCOME] Class ID: ${resolvedClassIdForLog || 'EMPTY'}, Class Name: ${resolvedClassNameForLog || 'EMPTY'}`);
        }
    }, [db, currentUserId, schoolId, isParentRole, currentSchoolYear, setError]);

    useEffect(() => {
        resolveIdentity();
    }, [resolveIdentity]);

    // Gérer le changement d'enfant sélectionné (pour les parents)
    const handleChildSelection = useCallback((childId) => {
        const child = children.find(c => c.id === childId);
        if (child) {
            setSelectedChildId(childId);
            setClassIdToFetch(child.classId || ''); // Assurez-vous que child.classId est bien l'ID de la classe
            setClassNameToDisplay(child.className || 'N/A');
            console.log(`[DEBUG - Parent Selection] Enfant sélectionné: ${child.name}, Nouvel ID de la classe à charger: ${child.classId}, Nouveau nom de la classe: ${child.className}`);
        }
        setIsDropdownOpen(false);
    }, [children]);


    // ----------------------------------------------------------------------------------
    // --- Écouteur de Données Principal (Emploi du Temps) ---
    // ----------------------------------------------------------------------------------
    useEffect(() => {
        // Bloquer le chargement tant que l'identité n'est pas résolue ou qu'il n'y a pas de Class ID
        if (isResolvingIdentity || !classIdToFetch) {
            setIsLoading(false);
            setEdtItems({});
            if (!classIdToFetch && !isResolvingIdentity) { // Ajout de cette condition pour éviter le log initial
                console.log(`[DEBUG - EDT Fetch] Skipping fetch because classIdToFetch is empty and identity resolution is complete.`);
            }
            return;
        }

        setIsLoading(true);
        setError('');
        console.log(`[DEBUG - EDT Fetch] Attempting to fetch EDT for classIdToFetch: ${classIdToFetch}`);


        try {
            const edtRef = collection(db, `schools/${schoolId}/emploidutemps`);

            // On filtre par classeId (CORRIGÉ ICI)
            const q = query(
                edtRef,
                where('classeId', '==', classIdToFetch), // Changé de 'classId' à 'classeId'
                orderBy('jourSemaine', 'asc'), // Changé de 'jour' à 'jourSemaine'
                orderBy('heureDebut', 'asc')
            );

            const unsubscribe = onSnapshot(q, (snapshot) => {
                const fetchedRecords = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                }));

                // Groupement par jour (CORRIGÉ ICI)
                const groupedByDay = fetchedRecords.reduce((acc, item) => {
                    const day = item.jourSemaine; // Changé de 'item.jour' à 'item.jourSemaine'
                    if (day) {
                        if (!acc[day]) {
                            acc[day] = [];
                        }
                        acc[day].push(item);
                    }
                    return acc;
                }, {});

                setEdtItems(groupedByDay);
                setIsLoading(false);
                console.log(`[DEBUG - EDT Fetch] EDT loaded successfully for classId: ${classIdToFetch}. Items count: ${fetchedRecords.length}`);
            }, (error) => {
                console.error("Erreur de chargement de l'EDT:", error);
                setError("Erreur lors du chargement de l'emploi du temps.");
                setIsLoading(false);
            });

            return () => unsubscribe();
        } catch (e) {
            console.error("Erreur de setup Firestore pour EDT:", e);
            setError("Erreur de configuration de la base de données.");
            setIsLoading(false);
        }
    }, [db, schoolId, classIdToFetch, isResolvingIdentity, setError]);


    // ----------------------------------------------------------------------------------
    // --- BLOC DE RENDU SÉQUENTIEL ---
    // ----------------------------------------------------------------------------------

    // Debug Info (à retirer une fois le problème résolu)
    {/*
    <div className="p-2 bg-blue-100 text-blue-800 rounded-md mb-4 mt-4">
        <h4 className="font-bold">DEBUG INFO:</h4>
        <p>User Role: <span className="font-semibold">{userRole}</span></p>
        <p>Current User ID: <span className="font-semibold">{currentUserId}</span></p>
        <p>Is Resolving Identity: <span className="font-semibold">{isResolvingIdentity ? 'Yes' : 'No'}</span></p>
        <p>Class ID to Fetch: <span className="font-semibold">{classIdToFetch || 'N/A'}</span></p>
        <p>Class Name to Display: <span className="font-semibold">{classNameToDisplay || 'N/A'}</span></p>
        {isParentRole && selectedChildId && <p>Selected Child ID: <span className="font-semibold">{selectedChildId}</span></p>}
        <p>Current School Year: <span className="font-semibold">{currentSchoolYear}</span></p>
    </div>
    */}

    // 1. Chargement de l'identité
    if (isResolvingIdentity) {
        return (
            <div className="flex justify-center items-center h-48">
                <Loader2 className="w-8 h-8 text-indigo-500 animate-spin mr-3" />
                <p className="text-gray-600">
                    {isParentRole ? "Recherche des enfants et de leurs classes..." : "Identification de votre classe..."}
                </p>
            </div>
        );
    }

    const isClassResolved = !!classIdToFetch;

    // 2. Classe non trouvée
    if (!isClassResolved && (isParentRole ? children.length === 0 : true)) {
         return (
            <div className="p-10 text-center bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-xl shadow-lg max-w-4xl mx-auto">
                <AlertTriangle className="w-8 h-8 mx-auto mb-3" />
                <p className="text-lg font-semibold">
                    {isParentRole ? "Aucune Classe d'Enfant Actif Trouvée" : "Classe Non Identifiée"}
                </p>
                <p>
                    {isParentRole
                        ? "Votre compte n'est pas lié à un enfant actif ou aucun enfant n'a d'inscription pour l'année en cours."
                        : `Votre compte élève n'est pas lié à une inscription pour l'année scolaire ${currentSchoolYear}.`}
                </p>
            </div>
        );
    }

    // 3. Chargement de l'EDT
    if (isLoading && classIdToFetch) {
         return (
            <div className="flex justify-center items-center h-48">
                <Loader2 className="w-8 h-8 text-indigo-500 animate-spin mr-3" />
                <p className="text-gray-600">
                    Chargement de l'emploi du temps pour la classe **{classNameToDisplay}**...
                </p>
            </div>
        );
    }

    // 4. Rendu de l'EDT
    return (
        <div className="space-y-6 p-4">
            {/* Debug Info (à retirer une fois le problème résolu) */}
            {/* Décommentez le bloc suivant pour voir les informations de débogage dans l'interface utilisateur */}
            {/*
            <div className="p-2 bg-blue-100 text-blue-800 rounded-md mb-4 mt-4">
                <h4 className="font-bold">DEBUG INFO:</h4>
                <p>User Role: <span className="font-semibold">{userRole}</span></p>
                <p>Current User ID: <span className="font-semibold">{currentUserId}</span></p>
                <p>Is Resolving Identity: <span className="font-semibold">{isResolvingIdentity ? 'Yes' : 'No'}</span></p>
                <p>Class ID to Fetch: <span className="font-semibold">{classIdToFetch || 'N/A'}</span></p>
                <p>Class Name to Display: <span className="font-semibold">{classNameToDisplay || 'N/A'}</span></p>
                {isParentRole && selectedChildId && <p>Selected Child ID: <span className="font-semibold">{selectedChildId}</span></p>}
                <p>Current School Year: <span className="font-semibold">{currentSchoolYear}</span></p>
            </div>
            */}

            <div className='flex flex-col md:flex-row justify-between items-start md:items-center border-b pb-2 mb-4'>
                <h3 className="text-2xl font-semibold text-gray-800 flex items-center mb-4 md:mb-0">
                    <Calendar className="w-6 h-6 mr-2 text-indigo-600" />
                    Emploi du Temps - Classe **{classNameToDisplay}**
                </h3>

                {/* SÉLECTEUR D'ENFANT (UNIQUEMENT POUR PARENT) */}
                {isParentRole && children.length > 1 && (
                     <div className="relative w-full md:w-64 z-10">
                        <button
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            className="w-full text-left bg-indigo-50 border border-indigo-300 rounded-xl p-3 flex items-center justify-between shadow-sm hover:border-indigo-500 transition duration-150"
                            title="Sélectionner un enfant"
                        >
                            <div className='flex items-center'>
                                <Filter className="w-5 h-5 mr-2 text-indigo-600" />
                                <span className="font-semibold text-gray-800">
                                    {children.find(c => c.id === selectedChildId)?.name || "Choisir un enfant"}
                                </span>
                            </div>
                            <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${isDropdownOpen ? 'rotate-180' : 'rotate-0'}`} />
                        </button>

                        {isDropdownOpen && (
                            <div className="absolute w-full mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 max-h-60 overflow-y-auto">
                                {children.map(child => (
                                    <div
                                        key={child.id}
                                        onClick={() => handleChildSelection(child.id)}
                                        className={`p-3 cursor-pointer hover:bg-indigo-100 flex items-center justify-between transition duration-150 ${selectedChildId === child.id ? 'bg-indigo-200 font-bold' : ''}`}
                                    >
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">{child.name}</p>
                                            <p className="text-xs text-gray-500">Classe: {child.className}</p>
                                        </div>
                                        {selectedChildId === child.id && <Check className="w-4 h-4 text-indigo-600" />}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* GRILLE DE L'EMPLOI DU TEMPS */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">

                {JoursDeLaSemaine.map((jour) => {
                    const dailyItems = edtItems[jour] || [];

                    return (
                        <div
                            key={jour}
                            className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
                        >
                            <div className={`p-4 font-bold text-lg text-white ${jour === 'Samedi' ? 'bg-orange-500' : 'bg-indigo-600'} flex items-center`}>
                                <Calendar className="w-5 h-5 mr-2" />
                                {jour}
                            </div>

                            {dailyItems.length > 0 ? (
                                <ul className="divide-y divide-gray-200">
                                    {dailyItems.map((item) => (
                                        <li
                                            key={item.id}
                                            className="p-4 hover:bg-indigo-50 transition duration-150"
                                        >
                                            <div className="flex justify-between items-start mb-1">
                                                <p className="text-md font-semibold text-gray-800 flex items-center">
                                                    <BookOpen size={16} className="mr-2 text-indigo-500" />
                                                    {item.matiere || 'Activité non spécifiée'} {/* CORRIGÉ ICI */}
                                                </p>
                                                <span className="text-sm text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                                                    {item.heureDebut} - {item.heureFin}
                                                </span>
                                            </div>

                                            <div className="text-sm text-gray-500 space-y-0.5 ml-6">
                                                <p className='flex items-center'>
                                                    <UserCheck size={14} className="mr-1 text-green-500" />
                                                    **Prof:** {item.enseignant || 'À déterminer'} {/* CORRIGÉ ICI */}
                                                </p>
                                                <p className='flex items-center'>
                                                    <Info size={14} className="mr-1 text-blue-500" />
                                                    **Salle:** {item.salle || 'N/A'}
                                                </p>
                                            </div>
                                        </li>
                                    ))}
                                </ul>

                            ) : (
                                <p className="p-8 text-center text-gray-500 flex flex-col items-center">
                                    <Clock className='w-6 h-6 mb-2 text-gray-400'/>
                                    Pas de cours prévu.
                                </p>
                            )}
                        </div>
                    );
                })}
            </div>

             <p className='text-xs text-gray-400 p-2 border-t mt-4'>
                **Note technique:** La requête Firestore est effectuée sur la collection `emploisDuTemps` avec le filtre **`classeId`** égal à **`{classIdToFetch || 'N/A'}`**.
            </p>
        </div>
    );
};

export default MyEmploiDuTemps;
